import React, { useState } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2, MessageCircle, Phone } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CLASSES, BOARDS, ADMISSION_STEPS, INSTITUTE } from "@/lib/content";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ============================================================
 *  EmailJS configuration
 *  ------------------------------------------------------------
 *  1. Create a free account at https://www.emailjs.com/
 *  2. Add a Gmail service → copy SERVICE_ID
 *  3. Create a template that uses variables:
 *       {{name}} {{phone}} {{email}} {{student_class}} {{board}} {{message}}
 *     Set the template "To Email" to: kothariclassesjalgaon@gmail.com
 *     Copy TEMPLATE_ID
 *  4. Copy your PUBLIC_KEY from Account → API Keys
 *  5. Add the values to /app/frontend/.env (then restart frontend):
 *       REACT_APP_EMAILJS_SERVICE_ID=your_service_id
 *       REACT_APP_EMAILJS_TEMPLATE_ID=your_template_id
 *       REACT_APP_EMAILJS_PUBLIC_KEY=your_public_key
 *  ------------------------------------------------------------
 *  The hard-coded fallbacks below are safe placeholders – REPLACE
 *  them with your real IDs or (preferred) use the env variables.
 * ============================================================
 */
const EMAILJS_SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID  || "YOUR_SERVICE_ID";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "YOUR_TEMPLATE_ID";
const EMAILJS_PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY  || "YOUR_PUBLIC_KEY";

export default function Admissions() {
  useSEO({
    title: "Admissions 2026–27 | Kothari Classes, Jalgaon — JEE, NEET, MH-CET",
    description: "Admissions open 2026–27 at Kothari Classes, Jalgaon. Book a free demo for JEE, NEET, MH-CET and Foundation (8th–12th) coaching. Baliram Peth & Yogeshwar Nagar.",
    canonical: "https://www.kothariclasses.in/admissions",
  });
  const [form, setForm] = useState({
    name: "", phone: "", email: "", student_class: "", board: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v?.target?.value ?? v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.student_class || !form.board) {
      toast.error("Please fill name, phone, class and board.");
      return;
    }
    if (!/^[+0-9\s-]{7,20}$/.test(form.phone)) {
      toast.error("Please enter a valid phone number.");
      return;
    }

    setLoading(true);

    // 1. Send email to kothariclassesjalgaon@gmail.com via EmailJS
    const emailPayload = {
      name: form.name,
      phone: form.phone,
      email: form.email || "(not provided)",
      student_class: form.student_class,
      board: form.board,
      message: form.message || "(no message)",
      to_email: "kothariclassesjalgaon@gmail.com",
    };

    let emailOk = false;
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        emailPayload,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      emailOk = true;
    } catch (err) {
      // EmailJS failure — fallthrough; we still try to save to DB below
      console.error("EmailJS send failed:", err);
    }

    // 2. Always save to MongoDB as a backup record (keeps admin dashboard working)
    let dbOk = false;
    try {
      await axios.post(`${API}/inquiries`, form);
      dbOk = true;
    } catch (err) {
      console.error("DB save failed:", err);
    }

    setLoading(false);

    if (emailOk || dbOk) {
      setSubmitted(true);
      toast.success("Thank you! We will contact you soon.");
      setForm({ name: "", phone: "", email: "", student_class: "", board: "", message: "" });
    } else {
      toast.error("Could not send your inquiry. Please try again or WhatsApp us.");
    }
  };

  const sendToWhatsApp = () => {
    const text = `Hi, I'd like to inquire about admissions.\nName: ${form.name || "-"}\nClass: ${form.student_class || "-"}\nBoard: ${form.board || "-"}\nPhone: ${form.phone || "-"}`;
    window.open(`https://wa.me/${INSTITUTE.whatsapp_number}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div data-testid="admissions-page">
      {/* Header */}
      <section className="bg-white py-16 lg:py-20 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="eyebrow">Admissions · 2026 – 27</span>
            <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-gray-950 leading-[1]">
              Three steps to<br/> your child's best year.
            </h1>
            <p className="mt-5 text-gray-600 text-lg">Book a free demo, meet the faculty, and confirm your seat. It's that simple.</p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="bg-gray-50 py-16 lg:py-20" data-testid="admission-steps-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-5">
            {ADMISSION_STEPS.map((s, i) => (
              <div key={s.n} className="relative bg-white border border-gray-200 p-8 card-lift" data-testid={`admission-step-${i}`}>
                <div className="font-display text-7xl font-black text-red-50 absolute top-2 right-4 select-none">{s.n}</div>
                <div className="relative">
                  <div className="eyebrow">Step {s.n}</div>
                  <h3 className="font-display text-2xl font-bold text-gray-950 mt-2">{s.title}</h3>
                  <p className="text-gray-600 mt-2 text-sm">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-red-600 text-white px-6 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="discount-banner">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">Early-bird discount — up to 16.3% off on confirmed admissions this quarter.</span>
            </div>
            <span className="text-xs uppercase tracking-widest opacity-90">Limited seats</span>
          </div>

          {/* K-SAT Scholarship Block */}
          <div
            id="ksat-block"
            data-testid="ksat-block"
            className="mt-6 grid lg:grid-cols-12 gap-0 border border-gray-200 bg-white overflow-hidden"
          >
            <div className="lg:col-span-4 bg-gray-950 text-white p-6 lg:p-8 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-red-600/30 rounded-full blur-3xl pointer-events-none" />
              <span className="text-[10px] font-bold tracking-[0.22em] text-red-500 uppercase relative">Scholarship Test</span>
              <h3 className="font-display text-2xl lg:text-3xl font-black mt-2 leading-tight relative">
                K-SAT 2026
              </h3>
              <p className="text-xs uppercase tracking-widest text-gray-400 mt-1 relative">Kothari Scholastic Aptitude Test</p>
              <div className="mt-6 pt-4 border-t border-white/10 relative">
                <div className="text-[10px] font-bold tracking-widest uppercase text-red-500">Open for</div>
                <div className="font-display text-xl font-bold text-white mt-1">8th · 9th · 10th · 11th · 12th</div>
              </div>
            </div>

            <div className="lg:col-span-8 p-6 lg:p-8 flex flex-col justify-between gap-5">
              <div>
                <h4 className="font-display text-xl lg:text-2xl font-bold text-gray-950 leading-tight">
                  K-SAT Scholarship Test 2026
                </h4>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                  Register for K-SAT and get a chance to earn scholarship-based admission benefits at Kothari Classes.
                </p>
                <ul className="grid sm:grid-cols-3 gap-3 mt-5">
                  {[
                    "Scholarship on tuition fees",
                    "Open for 8th, 9th, 10th, 11th & 12th",
                    "Limited seats available",
                  ].map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-gray-800">
                      <CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-wrap gap-3 pt-2 border-t border-gray-100">
                <a
                  href={`tel:${INSTITUTE.phone_raw}`}
                  data-testid="ksat-call-button"
                  className="btn-primary rounded-none text-sm"
                >
                  <Phone className="w-4 h-4" />
                  Register Now · {INSTITUTE.phone_short}
                </a>
                <button
                  type="button"
                  onClick={() => {
                    const el = document.querySelector('[data-testid="inquiry-form-section"]');
                    if (el) {
                      const top = el.getBoundingClientRect().top + window.scrollY - 80;
                      window.scrollTo({ top, behavior: "smooth" });
                    }
                  }}
                  data-testid="ksat-form-button"
                  className="btn-secondary rounded-none text-sm"
                >
                  Use Inquiry Form <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="bg-white py-20" data-testid="inquiry-form-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <span className="eyebrow">Inquiry form</span>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-gray-950">Tell us about your child.</h2>
            <p className="text-gray-600">Share a few details and our admissions team will reach out within 24 hours with batch availability, fee structure and a free demo slot.</p>
            <div className="border-t border-gray-200 pt-6 space-y-3 text-sm">
              <a href={`tel:${INSTITUTE.phone_raw}`} className="flex items-center gap-3 text-gray-900 hover:text-red-600" data-testid="admission-call-link">
                <Phone className="w-4 h-4 text-red-600" /> {INSTITUTE.phone}
              </a>
              <button type="button" onClick={sendToWhatsApp} className="flex items-center gap-3 text-gray-900 hover:text-red-600" data-testid="admission-whatsapp-link">
                <MessageCircle className="w-4 h-4 text-red-600" /> Chat on WhatsApp
              </button>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div className="border border-green-200 bg-green-50 p-10 text-center space-y-4" data-testid="inquiry-success">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
                <h3 className="font-display text-2xl font-bold text-gray-950">Thank you!</h3>
                <p className="text-gray-600">We will contact you soon. Want to chat faster?</p>
                <button onClick={sendToWhatsApp} className="btn-primary rounded-none text-sm mx-auto" data-testid="success-whatsapp-btn">
                  <MessageCircle className="w-4 h-4" /> Message on WhatsApp
                </button>
                <button onClick={() => setSubmitted(false)} className="block mx-auto text-xs uppercase tracking-widest text-gray-500 hover:text-gray-900" data-testid="submit-another">
                  Submit another inquiry →
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="border border-gray-200 p-6 sm:p-8 space-y-5 bg-white" data-testid="inquiry-form" noValidate>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-1.5 block">Student Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Full name"
                      data-testid="form-name-input"
                      className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-1.5 block">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+91 ..."
                      data-testid="form-phone-input"
                      className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-1.5 block">Class *</label>
                    <Select value={form.student_class} onValueChange={(v) => setForm((f) => ({ ...f, student_class: v }))}>
                      <SelectTrigger data-testid="form-class-select" className="rounded-none border-gray-300 focus:ring-red-600 focus:border-red-600 py-3 h-auto">
                        <SelectValue placeholder="Select class" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {CLASSES.map((c) => (
                          <SelectItem key={c} value={c} data-testid={`form-class-option-${c}`}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-1.5 block">Board *</label>
                    <Select value={form.board} onValueChange={(v) => setForm((f) => ({ ...f, board: v }))}>
                      <SelectTrigger data-testid="form-board-select" className="rounded-none border-gray-300 focus:ring-red-600 focus:border-red-600 py-3 h-auto">
                        <SelectValue placeholder="Select board" />
                      </SelectTrigger>
                      <SelectContent>
                        {BOARDS.map((b) => (
                          <SelectItem key={b} value={b} data-testid={`form-board-option-${b}`}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-1.5 block">Email (optional)</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="parent@email.com"
                    data-testid="form-email-input"
                    className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-1.5 block">Message (optional)</label>
                  <textarea
                    rows={3}
                    name="message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Any specific question or preferred batch timing?"
                    data-testid="form-message-input"
                    className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none resize-none"
                  />
                </div>

                <div className="flex flex-wrap gap-3 pt-2">
                  <button type="submit" disabled={loading} className="btn-primary rounded-none text-sm disabled:opacity-60" data-testid="form-submit-button">
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Submit Inquiry <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  <button type="button" onClick={sendToWhatsApp} className="btn-secondary rounded-none text-sm" data-testid="form-whatsapp-button">
                    <MessageCircle className="w-4 h-4" /> Send on WhatsApp
                  </button>
                </div>
                <p className="text-xs text-gray-500">By submitting, you agree to be contacted by Kothari Classes. We never share your information.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
